import React from "react"
import { Plus, RotateCcw } from "lucide-react"

const MIN_SAVE_SPINNER_MS = 300

export default function OrderNotes({
    title,
    notes,
    helperText = "",
    placeholder = "",
    onSave = async (_notes) => {},
    onStartEdit = () => true,
    normalizeNotes = (value) => value ?? "",
    notesHaveChanges = (original, draft) => draft !== original,
    prepareForSave = (value) => value,
    beginEditing = (value) => value,
}) {
    const textareaRef = React.useRef(/** @type {HTMLTextAreaElement | null} */ (null))
    const [isEditing, setIsEditing] = React.useState(false)
    const [isSaving, setIsSaving] = React.useState(false)
    const rawNotes = notes ?? ""
    const displayNotes = normalizeNotes(rawNotes)
    const [draftNotes, setDraftNotes] = React.useState(displayNotes)

    React.useEffect(() => {
        if (!isEditing) {
            setDraftNotes(displayNotes)
        }
    }, [displayNotes, isEditing])

    React.useEffect(() => {
        if (!isEditing || !textareaRef.current) {
            return
        }
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(
            textareaRef.current.value.length,
            textareaRef.current.value.length
        )
    }, [isEditing])

    const hasChanges = notesHaveChanges(rawNotes, draftNotes)

    async function handleSave() {
        if (isSaving) {
            return
        }
        const startedAtMs = Date.now()
        setIsSaving(true)
        try {
            await onSave(prepareForSave(draftNotes))
            const elapsed = Date.now() - startedAtMs
            const waitMs = MIN_SAVE_SPINNER_MS - elapsed
            if (waitMs > 0) {
                await new Promise((resolve) => window.setTimeout(resolve, waitMs))
            }
            setIsEditing(false)
        } finally {
            setIsSaving(false)
        }
    }

    function revertDraft() {
        setDraftNotes(displayNotes)
        setIsEditing(false)
    }

    return (
        <div className="card rounded shadow-sm h-100 w-100 mb-0"
            style={{ backgroundColor: "#d4edda" }}>
            <div className="card-header bg-transparent border-bottom d-flex justify-content-between align-items-center py-3">
                <div>
                    <h3 className="fs-5 fw-semibold text-dark mb-0">{title}</h3>
                    {helperText ? (
                        <p className="text-secondary mb-0 mt-1" style={{ fontSize: "0.875rem" }}>
                            {helperText}
                        </p>
                    ) : null}
                </div>
                {!isEditing ? (
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
                        onClick={() => {
                            const ok = onStartEdit()
                            if (ok === false) {
                                return
                            }
                            setDraftNotes(beginEditing(displayNotes))
                            setIsEditing(true)
                        }}
                    >
                        <span className="me-1 d-inline-flex"><Plus size={14} /></span>
                        Add Notes
                    </button>
                ) : (
                    <div className="d-inline-flex align-items-center gap-2">
                        {hasChanges ? (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-light btn-sm border border-secondary-subtle d-inline-flex
                                        align-items-center justify-content-center"
                                    style={{ height: "31px", borderRadius: "0.375rem" }}
                                    title="Revert changes"
                                    aria-label="Revert changes"
                                    disabled={isSaving}
                                    onClick={revertDraft}
                                >
                                    <RotateCcw size={14} />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm fw-semibold"
                                    style={{ borderRadius: "0.375rem" }}
                                    disabled={isSaving}
                                    onClick={() => {
                                        void handleSave()
                                    }}
                                >
                                    {isSaving ? "Saving Notes..." : "Save Notes"}
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                disabled={isSaving}
                                onClick={revertDraft}
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                )}
            </div>
            <div className="card-body p-4">
                {isEditing ? (
                    <textarea
                        ref={textareaRef}
                        className="form-control fs-6 text-dark"
                        style={{
                            minHeight: "100px",
                            backgroundColor: "#faf9d9",
                            resize: "vertical",
                        }}
                        value={draftNotes}
                        placeholder={placeholder}
                        disabled={isSaving}
                        onChange={(event) => setDraftNotes(event.currentTarget.value)}
                    />
                ) : (
                    <div
                        className="fs-6 text-dark"
                        style={{
                            minHeight: displayNotes ? undefined : "100px",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                        {displayNotes ? displayNotes : (
                            <span className="text-secondary fst-italic">
                                {placeholder || "No notes added yet"}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
